// components/WizardAside.tsx
import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { formatMoney, isQuoteOnly } from "@shared/commission";
import { Receipt } from "./Receipt";
import { ArtistChecklist, useChecklist } from "./ArtistChecklist";
import { useEstimate } from "../hooks/useEstimate";
import { useIntakeCopy } from "../i18n/useIntakeCopy";
import { CountUp, ProgressRing } from "../motion/primitives";
import { DUR, EASE_ENTER } from "../motion/tokens";
import type { IntakeArtist } from "../types";

export function WizardAside({ artist }: { artist: IntakeArtist }) {
  return (<><Receipt artist={artist} /><ArtistChecklist artist={artist} /></>);
}

export function MobileWizardBar({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const [open, setOpen] = useState(false);
  const estimate = useEstimate(artist);
  const { done, total } = useChecklist(artist);
  return (
    <div className="lg:hidden">
      <AnimatePresence>
        {open && (
          <m.div key="sheet" initial={{ y: "100%" }} animate={{ y: 0, transition: { duration: DUR.scene, ease: EASE_ENTER } }} exit={{ y: "100%" }}
            className="fixed inset-x-0 bottom-16 z-40 max-h-[70vh] space-y-3 overflow-y-auto rounded-t-2xl border-t border-[var(--color-border)] bg-[var(--color-bg)] p-4">
            <WizardAside artist={artist} />
          </m.div>
        )}
      </AnimatePresence>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="fixed inset-x-0 bottom-0 z-50 flex h-16 items-center gap-3 border-t border-[var(--color-border)] bg-[var(--color-bg-card)] px-4">
        <ProgressRing value={done / total} size={36} />
        <span className="text-xs text-[var(--color-text-muted)]">{fmt(c.checklist.progress, { done, total })}</span>
        <span className="ml-auto text-right">
          <span className="block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">{c.receipt.total}</span>
          <span className="text-lg font-semibold text-[var(--color-primary)]">{isQuoteOnly(estimate) ? c.receipt.ask : <CountUp value={estimate.total} format={formatMoney} />}</span>
        </span>
        <m.span animate={{ rotate: open ? 180 : 0 }}><ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" /></m.span>
      </button>
    </div>
  );
}
